/**
 * Base API client with cookie-based authentication
 *
 * Uses BFF pattern - session cookie is automatically included
 * via credentials: 'include'. No Bearer token needed.
 */

// Use nullish coalescing to allow empty string as valid value
const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Token getter removed - BFF handles all authentication server-side

/**
 * Fetch wrapper with cookie-based authentication
 * Session cookie is automatically included via credentials: 'include'
 * No Bearer token needed - BFF handles auth server-side
 */
export async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Include session cookie for BFF auth
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    let errorDetails: unknown;

    try {
      const errorBody = await response.json();
      errorMessage = errorBody.error || errorBody.message || errorMessage;
      errorDetails = errorBody;
    } catch {
      // Response wasn't JSON
    }

    // For 401/403 on non-auth endpoints, don't auto-redirect
    // Let React Query handle the error so the UI can display it
    // The AuthProvider will handle session expiration separately
    throw new ApiError(response.status, errorMessage, errorDetails);
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return response.json();
  }

  return {} as T;
}

export function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      searchParams.set(key, String(value));
    }
  }

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}
